import {
  getCanonicalSecurityGroupKey,
  type CanonicalSecurityEntry,
} from "@shared/security-canonical";
export { getCanonicalSecurityGroupKey } from "@shared/security-canonical";

export interface DealSecurityRegisterEntry extends CanonicalSecurityEntry {
  id: string;
  facilityId?: string | null;
  facilityName?: string | null;
}

type LinkedFacility = {
  facilityId: string;
  facilityName: string;
};

export type CanonicalDealSecurity<T extends DealSecurityRegisterEntry> = Omit<T, "linkedFacilities"> & {
  primaryFacilityId: string | null;
  linkedFacilities: LinkedFacility[];
};

function sortLinkedFacilities(linkedFacilities: LinkedFacility[]): LinkedFacility[] {
  return [...linkedFacilities].sort((left, right) => {
    const nameCompare = left.facilityName.localeCompare(right.facilityName);
    if (nameCompare !== 0) return nameCompare;
    return left.facilityId.localeCompare(right.facilityId);
  });
}

export function getCrossCollateralSecurityIds<T extends DealSecurityRegisterEntry>(
  securities: T[],
): Set<string> {
  const counts = new Map<string, number>();
  const idsByGroupKey = new Map<string, string[]>();

  for (const security of securities) {
    const groupKey = getCanonicalSecurityGroupKey(security);
    counts.set(groupKey, (counts.get(groupKey) ?? 0) + 1);

    if (!idsByGroupKey.has(groupKey)) idsByGroupKey.set(groupKey, []);
    const ids = idsByGroupKey.get(groupKey)!;
    if (!ids.includes(security.id)) ids.push(security.id);
  }

  const ids = new Set<string>();
  counts.forEach((count, groupKey) => {
    if (count <= 1) return;
    for (const id of idsByGroupKey.get(groupKey) ?? []) {
      ids.add(id);
    }
  });

  return ids;
}

export function buildLinkedFacilitiesMap<T extends DealSecurityRegisterEntry>(
  securities: T[],
  facilityNameMap: Record<string, string>,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  const facilityCountsByGroupKey = new Map<string, number>();

  for (const security of securities) {
    const groupKey = getCanonicalSecurityGroupKey(security);
    facilityCountsByGroupKey.set(groupKey, (facilityCountsByGroupKey.get(groupKey) ?? 0) + 1);
  }

  for (const security of securities) {
    const groupKey = getCanonicalSecurityGroupKey(security);
    if ((facilityCountsByGroupKey.get(groupKey) ?? 0) <= 1) continue;
    if (!security.facilityId) continue;

    const facilityName =
      security.facilityName ||
      facilityNameMap[security.facilityId] ||
      `Facility ${security.facilityId.slice(0, 6)}`;

    if (!map[groupKey]) map[groupKey] = [];
    if (!map[groupKey].includes(facilityName)) map[groupKey].push(facilityName);
  }

  return map;
}

export function buildCanonicalDealSecurities<T extends DealSecurityRegisterEntry>(
  securities: T[],
  facilityNameMap: Record<string, string>,
): CanonicalDealSecurity<T>[] {
  const byCanonicalKey = new Map<string, CanonicalDealSecurity<T>>();

  for (const security of securities) {
    const facilityName = security.facilityId
      ? security.facilityName ||
        facilityNameMap[security.facilityId] ||
        `Facility ${security.facilityId.slice(0, 6)}`
      : null;

    const canonicalKey = getCanonicalSecurityGroupKey(security);
    const existing = byCanonicalKey.get(canonicalKey);
    if (!existing) {
      byCanonicalKey.set(canonicalKey, {
        ...security,
        primaryFacilityId: security.facilityId ?? null,
        linkedFacilities: security.facilityId && facilityName
          ? [
              {
                facilityId: security.facilityId,
                facilityName,
              },
            ]
          : [],
      });
      continue;
    }

    if (!security.facilityId || !facilityName) continue;

    if (existing.primaryFacilityId === null) {
      existing.primaryFacilityId = security.facilityId;
    }

    if (!existing.linkedFacilities.some((facility) => facility.facilityId === security.facilityId)) {
      existing.linkedFacilities.push({
        facilityId: security.facilityId,
        facilityName,
      });
    }
  }

  return Array.from(byCanonicalKey.values()).map((security) => {
    const linkedFacilities = sortLinkedFacilities(security.linkedFacilities);
    return {
      ...security,
      linkedFacilities,
      primaryFacilityId: linkedFacilities[0]?.facilityId ?? null,
    };
  });
}
